import React, { useState, useEffect } from 'react';
import Navigation from './components/Navigation';
import PersonalInfo from './components/PersonalInfo';
import IndoorUsage from './components/IndoorUsage';
import OutdoorUsage from './components/OutdoorUsage';
import Results from './components/Results';
import WaterFootprintLanding from './components/WaterFootprintLanding';
import { saveResult } from './components/api';
import './App.css';

function App() {
  const [showCalculator, setShowCalculator] = useState(false);
  const [activeBucket, setActiveBucket] = useState('personal');
  const [formData, setFormData] = useState({
    personal: {},
    indoor: {},
    outdoor: {},
  });
  const [isPersonalInfoValid, setIsPersonalInfoValid] = useState(false);

  // Extract query parameters on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const companyParam = urlParams.get('company');
    
    if (companyParam) {
      updateFormData('personal', { company: companyParam });
    }
  }, []);

  const handleGetStarted = () => {
    setShowCalculator(true);
    setActiveBucket('personal');
  };

  const updateFormData = (section, data) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        ...data
      }
    }));
  };

  const resetCalculator = () => {
    setFormData({
      personal: {},
      indoor: {},
      outdoor: {},
    });
    setShowCalculator(false);
    setActiveBucket('personal');
    setIsPersonalInfoValid(false);
  };

  const handleNavigationClick = (bucket) => {
    const sections = ['personal', 'indoor', 'outdoor', 'result'];
    const currentIndex = sections.indexOf(activeBucket);
    const targetIndex = sections.indexOf(bucket);
    
    // Only check validation when moving forward from personal section
    if (currentIndex === 0 && targetIndex > currentIndex && !isPersonalInfoValid) {
      alert('Please fill in all required fields (Name and Email) before proceeding.');
      return;
    }
    
    if (targetIndex <= currentIndex + 1) {
      setActiveBucket(bucket);
    }
  };

  const renderSection = () => {
    switch (activeBucket) {
      case 'personal':
        return <PersonalInfo 
                formData={formData.personal} 
                updateFormData={updateFormData}
                onValidationChange={setIsPersonalInfoValid}
              />;
      case 'indoor':
        return <IndoorUsage 
                formData={formData.indoor}
                updateFormData={updateFormData} 
              />;
      case 'outdoor':
        return <OutdoorUsage 
                formData={formData.outdoor}
                updateFormData={updateFormData} 
              />;
      case 'result':
        return <Results 
                formData={formData}
                resetCalculator={resetCalculator} 
              />;
      default:
        return <PersonalInfo 
                formData={formData.personal}
                updateFormData={updateFormData} 
              />;
    }
  };

  const handleNextClick = () => {
    const sections = ['personal', 'indoor', 'outdoor', 'result'];
    const currentIndex = sections.indexOf(activeBucket);
    
    // Check validation only when on personal section
    if (currentIndex === 0 && !isPersonalInfoValid) {
      alert('Please fill in all required fields (Name and Email) before proceeding.');
      return;
    }
    
    if (currentIndex < sections.length - 1) {
      const nextSection = sections[currentIndex + 1];
      if (nextSection === 'result') saveResult(formData);
      setActiveBucket(nextSection);
    }
  };

  const handleBackClick = () => {
    const sections = ['personal', 'indoor', 'outdoor', 'result'];
    const currentIndex = sections.indexOf(activeBucket);
    
    if (currentIndex > 0) {
      setActiveBucket(sections[currentIndex - 1]);
    }
  };

  if (!showCalculator) {
    return <WaterFootprintLanding onGetStarted={handleGetStarted} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation 
        activeBucket={activeBucket}
        onNavigationClick={handleNavigationClick}
      />
      
      <div className="container mx-auto py-8 px-4">
        {renderSection()}
        
        {activeBucket !== 'result' && (
          <div className="flex justify-between mt-8 max-w-2xl mx-auto">
            <button
              onClick={handleBackClick}
              className={`px-4 py-2 rounded-lg text-gray-600 border border-gray-300 hover:bg-gray-100 ${
                activeBucket === 'personal' ? 'invisible' : ''
              }`}
            >
              Back
            </button>
            
            <button
              onClick={handleNextClick}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {activeBucket === 'outdoor' ? 'View Results' : 'Next'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;